import { motion } from "framer-motion";
import { Sofa, Wifi, BedDouble, Tv, UtensilsCrossed, Sparkles, WashingMachine, Laptop, MapPin, HeartHandshake, LogIn, LogOut, CigaretteOff, PawPrint, Users, Moon } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const ITEMS = [
  { icon: Sofa, label: "Fully Furnished", note: "Every room, ready on day one" },
  { icon: Wifi, label: "High-Speed Wi-Fi", note: "Work-friendly throughout" },
  { icon: BedDouble, label: "Premium Linens", note: "Plush bedding, hotel-grade" },
  { icon: Tv, label: "Smart TVs", note: "Stream and unwind" },
  { icon: UtensilsCrossed, label: "Full Kitchens", note: "Cook, gather, stay in" },
  { icon: Sparkles, label: "Professional Maid Service", note: "Every stay, every time" },
  { icon: WashingMachine, label: "Washer & Dryer", note: "In every residence" },
  { icon: Laptop, label: "Workspace Ready", note: "Room for remote work" },
  { icon: MapPin, label: "Conveniently Located", note: "Baker & Ascension Parish" },
  { icon: HeartHandshake, label: "Thoughtfully Hosted", note: "Cared for, every stay" },
];

export default function Amenities() {
  return (
    <section id="amenities" data-testid="amenities-section" className="bg-[#fbf8f0]/90 text-[#16301f]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-14 sm:mb-16 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#16301f]/60 flex items-center gap-4">
              <span className="h-px w-12 bg-[#16301f]/40 inline-block" />
              The Estate Standard
            </p>
            <h2 className="font-display font-medium text-4xl sm:text-5xl mt-6">Every stay includes</h2>
          </div>
          <p className="font-script text-3xl sm:text-4xl text-[#1a3324] -rotate-1">Comfort, as standard.</p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-t border-l border-[#16301f]/10">
          {ITEMS.map((item, i) => (
            <motion.div
              key={item.label}
              data-testid={`amenity-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 5) * 0.08, ease: EASE }}
              className="group border-b border-r border-[#16301f]/10 p-6 sm:p-8 hover:bg-[#f7f2e7] transition-colors duration-500"
            >
              <item.icon
                className="w-6 h-6 text-[#16301f] transition-transform duration-500 group-hover:-translate-y-1"
                strokeWidth={1.5}
              />
              <h3 className="font-display text-lg sm:text-xl mt-5">{item.label}</h3>
              <p className="text-xs text-[#16301f]/55 font-light mt-1.5">{item.note}</p>
            </motion.div>
          ))}
        </div>
        <motion.div
          data-testid="good-to-know"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-14 border border-[#16301f]/10 bg-[#f7f2e7]"
        >
          <p className="text-center text-[10px] uppercase tracking-[0.3em] text-[#16301f]/50 pt-6">
            Good to know
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 divide-x divide-[#16301f]/10">
            {[
              { icon: LogIn, label: "Check-in", value: "From 3:00 PM" },
              { icon: LogOut, label: "Check-out", value: "By 11:00 AM" },
              { icon: CigaretteOff, label: "Smoke-free", value: "Entire property" },
              { icon: PawPrint, label: "Pets", value: "With prior approval" },
              { icon: Users, label: "Guests", value: "Registered guests only" },
              { icon: Moon, label: "Good neighbors", value: "Quiet, respectful stays" },
            ].map((item) => (
              <div
                key={item.label}
                data-testid={`policy-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="px-4 py-6 text-center"
              >
                <item.icon className="w-5 h-5 text-[#16301f] mx-auto" strokeWidth={1.5} />
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#16301f]/50 mt-3">
                  {item.label}
                </p>
                <p className="font-display text-base sm:text-lg mt-1">{item.value}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
